/**
 * SSE streaming client for the `ask-cannafax` edge function.
 * Unchanged transport contract: POST { message, threadId, pageContext },
 * responds with an OpenAI-style SSE stream and an `X-Thread-Id` header.
 */

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ask-cannafax`;

export interface StreamChatArgs {
  message: string;
  threadId?: string;
  pageContext: unknown;
  attachments?: { name: string; text: string; truncated: boolean; earlier?: boolean }[];
  token: string;
  signal?: AbortSignal;
  onDelta: (text: string) => void;
  /** A connector/tool started running (e.g. Google Drive search). */
  onStatus?: (status: ToolStatus) => void;
  /** The assistant offered to turn an attached file into inventory listings. */
  onImportProposal?: (p: ImportProposal) => void;
  /** The assistant offered to create one draft listing from the conversation. */
  onDraftProposal?: (p: DraftProposal) => void;
  onBuyRequestProposal?: (p: BuyRequestProposal) => void;
  /** The assistant offered to remove entries from the user's saved memory. */
  onMemoryForgetProposal?: (p: MemoryForgetProposal) => void;
  onDone: () => void;
  onThreadId: (id: string) => void;
  onError: (err: string) => void;
}

/** What happened to an action card; saved with the conversation so the card stays in place. */
export interface CardOutcome {
  state: string;
  listingId?: string | null;
  lab?: string;
  uploadId?: string | null;
  draftCount?: number;
  error?: string | null;
  /** Memory-forget card: ids the user has already removed. */
  removedIds?: string[];
}

export interface ImportProposal {
  /** Server id for this card; lets its outcome be saved with the conversation. */
  cardId?: string;
  outcome?: CardOutcome;
  fileName: string;
  productsDetected: number | null;
  summary: string;
  missing: string[];
}

export interface BuyRequestProposal {
  cardId?: string;
  outcome?: CardOutcome;
  text: string;
}

export interface MemoryForgetEntry {
  id: string;
  kind: 'deal_outcome' | 'chat_takeaway' | 'preference' | 'commitment';
  content: string;
}

export interface MemoryForgetProposal {
  cardId?: string;
  outcome?: CardOutcome;
  entries: MemoryForgetEntry[];
}

export interface DraftProposal {
  cardId?: string;
  outcome?: CardOutcome;
  strainName: string;
  productForm: string | null;
  species: 'indica' | 'sativa' | 'hybrid' | null;
  quantityValue: number | null;
  quantityUnit: string | null;
  priceValue: number | null;
  priceUnit: string | null;
  thcPercent: number | null;
  cbdPercent: number | null;
  coaFileName: string | null;
  missing: string[];
}

export interface ToolStatus {
  tool: string;
  label: string;
}

export async function streamChat({
  message,
  threadId,
  pageContext,
  attachments,
  token,
  signal,
  onDelta,
  onStatus,
  onImportProposal,
  onDraftProposal,
  onBuyRequestProposal,
  onMemoryForgetProposal,
  onDone,
  onThreadId,
  onError,
}: StreamChatArgs) {
  try {
    const resp = await fetch(CHAT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ message, threadId, pageContext, attachments }),
      signal,
    });

    const newThreadId = resp.headers.get('X-Thread-Id');
    if (newThreadId) onThreadId(newThreadId);

    if (!resp.ok) {
      const errBody = await resp.json().catch(() => ({ error: 'Request failed' }));
      if (resp.status === 429) {
        onError('Too many requests right now. Give it a moment and try again.');
      } else if (resp.status === 402) {
        onError('The assistant is out of credits. Please contact support.');
      } else {
        onError(errBody.error || `Something went wrong (${resp.status}).`);
      }
      return;
    }

    if (!resp.body) {
      onError('No response stream');
      return;
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const consumeLine = (raw: string) => {
      let line = raw;
      if (line.endsWith('\r')) line = line.slice(0, -1);
      if (!line.startsWith('data: ')) return true;
      const jsonStr = line.slice(6).trim();
      if (jsonStr === '[DONE]') return false;
      try {
        const parsed = JSON.parse(jsonStr);
        if (parsed.type === 'status') {
          if (typeof parsed.label === 'string') onStatus?.({ tool: String(parsed.tool ?? ''), label: parsed.label });
          return true;
        }
        if (parsed.type === 'import_proposal') {
          if (typeof parsed.fileName === 'string') onImportProposal?.({
            cardId: typeof parsed.cardId === 'string' ? parsed.cardId : undefined,
            fileName: parsed.fileName,
            productsDetected: typeof parsed.productsDetected === 'number' ? parsed.productsDetected : null,
            summary: String(parsed.summary ?? ''),
            missing: Array.isArray(parsed.missing) ? parsed.missing.map(String) : [],
          });
          return true;
        }
        if (parsed.type === 'buy_request_proposal') {
          if (typeof parsed.text === 'string') onBuyRequestProposal?.({
            cardId: typeof parsed.cardId === 'string' ? parsed.cardId : undefined,
            text: parsed.text,
          });
          return true;
        }
        if (parsed.type === 'memory_forget_proposal') {
          const entries = Array.isArray(parsed.entries)
            ? parsed.entries
                .filter((e: unknown) => e && typeof (e as { content?: unknown }).content === 'string' && typeof (e as { id?: unknown }).id === 'string')
                .map((e: { id: string; kind?: string; content: string }) => ({
                  id: e.id,
                  kind: (['deal_outcome', 'chat_takeaway', 'preference', 'commitment'].includes(String(e.kind)) ? e.kind : 'chat_takeaway') as MemoryForgetEntry['kind'],
                  content: String(e.content).slice(0, 500),
                }))
            : [];
          if (entries.length) onMemoryForgetProposal?.({ cardId: typeof parsed.cardId === 'string' ? parsed.cardId : undefined, entries });
          return true;
        }
        if (parsed.type === 'draft_proposal') {
          if (typeof parsed.strainName === 'string') onDraftProposal?.(parsed as DraftProposal);
          return true;
        }
        const content = parsed.choices?.[0]?.delta?.content;
        if (content) onDelta(content);
        return true;
      } catch {
        return null; // partial chunk
      }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let newlineIndex: number;
      while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, newlineIndex);
        const rest = buffer.slice(newlineIndex + 1);
        if (line.startsWith(':') || line.trim() === '') {
          buffer = rest;
          continue;
        }
        const result = consumeLine(line);
        if (result === null) break; // wait for more bytes
        buffer = rest;
        if (result === false) break;
      }
    }

    if (buffer.trim()) {
      for (const raw of buffer.split('\n')) {
        if (!raw) continue;
        consumeLine(raw);
      }
    }

    onDone();
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      onDone();
      return;
    }
    onError(e instanceof Error ? e.message : 'Connection error');
  }
}